import SeoHead from './SeoHead';
import { Breadcrumbs } from './ComponentLibrary';
import { pageMeta } from '../seoUtils';

function AgentVerificationPolicyPage() {
  const seo = pageMeta({
    title: 'Agent Verification Policy',
    description:
      'What NaijaNest requires from agents and property professionals submitting information for verification, and what verification does and does not mean.',
    path: '/agent-verification-policy',
  });
  return (
    <div className="static-page agent-verification-policy-page">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Agent Verification Policy', current: true },
        ]}
      />
      <section className="section">
        <header className="agent-verification-policy-page__intro">
          <h1>Agent Verification Policy</h1>
          <p className="static-page__lead">
            Effective date: 8 August 2026. Last updated: 8 August 2026.
          </p>
        </header>

        <h2>1. Purpose</h2>
        <p>NaijaNest is committed to maintaining a trustworthy property marketplace.</p>
        <p>
          Where an agent or property professional is asked to provide information for
          verification, that information must be{' '}
          <strong>accurate, genuine, current, and submitted in good faith</strong>.
        </p>
        <p>Agents must not submit false, misleading, altered, or fraudulent information.</p>

        <h2>2. Information Submitted by Agents</h2>
        <p>
          Agents are responsible for ensuring that all information they provide to NaijaNest is
          truthful and accurate.
        </p>
        <p>This includes information relating to:</p>
        <ul>
          <li>their identity;</li>
          <li>their business or agency;</li>
          <li>their contact details;</li>
          <li>their professional activities;</li>
          <li>properties they advertise; and</li>
          <li>any documents or supporting information they submit.</li>
        </ul>
        <p>
          Agents must not knowingly provide information that is false, misleading, incomplete, or
          intended to deceive.
        </p>

        <h2>3. Authentic Documents and Information</h2>
        <p>
          Where NaijaNest requests supporting documentation, agents must submit genuine documents
          that relate to them or the business they represent.
        </p>
        <p>Agents must not:</p>
        <ul>
          <li>alter or falsify documents;</li>
          <li>submit stolen documents;</li>
          <li>use another person's identity;</li>
          <li>impersonate an individual or business;</li>
          <li>submit documents belonging to another person without appropriate authority;</li>
          <li>provide false registration or business information.</li>
        </ul>

        <h2>4. Authority to Represent</h2>
        <p>
          Agents are responsible for ensuring that they have the appropriate authority to
          represent any business, property owner, developer, landlord, or other party they claim
          to represent.
        </p>
        <p>
          Submitting information to NaijaNest does not itself establish or guarantee that an
          agent has authority to act on behalf of a property owner or other party.
        </p>

        <h2>5. Property Listings</h2>
        <p>
          Agent verification does not automatically verify the properties advertised by that
          agent.
        </p>
        <p>
          Agents remain responsible for ensuring that their property listings are genuine and
          that the information provided is accurate to the best of their knowledge.
        </p>
        <p>Agents must not knowingly advertise:</p>
        <ul>
          <li>properties that do not exist;</li>
          <li>properties they have no authority to advertise;</li>
          <li>false or misleading prices;</li>
          <li>false property descriptions;</li>
          <li>fraudulent documents;</li>
          <li>misleading photographs or other content.</li>
        </ul>

        <h2>6. NaijaNest Review</h2>
        <p>NaijaNest may review information submitted by agents where appropriate.</p>
        <p>
          A review or acceptance of submitted information does not mean that NaijaNest guarantees
          that the information is accurate, complete, current, or genuine.
        </p>
        <p>
          NaijaNest may request additional information or clarification where it has reasonable
          concerns about information submitted to the Platform.
        </p>

        <h2>7. Fraud and Misrepresentation</h2>
        <p>
          NaijaNest does not tolerate fraud, impersonation, deliberate misrepresentation, or the
          submission of fraudulent information.
        </p>
        <p>
          Where NaijaNest identifies or reasonably suspects fraudulent or misleading activity, it
          may take appropriate action, including:
        </p>
        <ul>
          <li>rejecting submitted information;</li>
          <li>removing or restricting listings;</li>
          <li>restricting account functionality;</li>
          <li>suspending an account;</li>
          <li>terminating an account; or</li>
          <li>reporting suspected unlawful activity to the appropriate authorities where required.</li>
        </ul>

        <h2>8. Changes to Submitted Information</h2>
        <p>Agents are responsible for keeping information provided to NaijaNest accurate and up to date.</p>
        <p>
          If previously submitted information becomes inaccurate or no longer applies, the agent
          should update the information where the Platform allows or contact NaijaNest through
          the available support channels.
        </p>

        <h2>9. No Guarantee or Endorsement</h2>
        <p>
          Any review of information submitted by an agent is intended to support trust and
          transparency on the Platform.
        </p>
        <p>It does not constitute:</p>
        <ul>
          <li>an endorsement of the agent;</li>
          <li>a guarantee of the agent's conduct;</li>
          <li>confirmation of property ownership;</li>
          <li>confirmation of title or legal rights;</li>
          <li>confirmation that an agent has exclusive authority over a property; or</li>
          <li>a guarantee of any transaction.</li>
        </ul>
        <p>Users should conduct their own independent checks before entering into any property transaction.</p>

        <h2>10. Reporting Concerns</h2>
        <p>
          Users who believe an agent has provided false information, impersonated another person
          or business, or engaged in fraudulent activity should report the matter to NaijaNest
          through the available reporting or contact channels.
        </p>
        <p>
          Where possible, reports should include the relevant listing, account, or other
          information that may help NaijaNest investigate the concern.
        </p>

        <h2>11. Policy Updates</h2>
        <p>NaijaNest may update this policy as its verification processes and Platform features develop.</p>
        <p>The latest version will be published on the Platform with the applicable effective date.</p>

        <h2>Important</h2>
        <p>
          NaijaNest is a digital property marketplace. Agents and other advertisers remain
          responsible for the information they submit and the properties they advertise.
        </p>
        <p>
          Users should always carry out appropriate independent due diligence before making
          payments or entering into a property agreement.
        </p>
      </section>
    </div>
  );
}
export default AgentVerificationPolicyPage;
